<template>
  <Drawer
    :on-close="closePanel"
    close-icon-position="floating"
    :close-title="I18n.t('editor.modal_close')"
    container-padding-class="!px-6 pt-6 pb-0"
  >
    <template #content>
      <div
        class="flex md:flex-col flex-col content-wrapper"
        :class="{ 'content-wrapper-legacy-editor': !isNewPhotoEditor }"
      >
        <TabGroup :onChange="onTabChanged" :selectedIndex="selectedTab">
          <div class="tab-list-scroll-container !px-1">
            <TabList
              v-if="!store.isBackgroundSearchActive"
              class="flex space-x-2 !pl-3 md:!pl-0 md:!pt-4 md:!pb-2"
              key="tabList"
            >
              <Tab v-if="isMagicBackgroundsEnabled" v-slot="{ selected }" as="template">
                <Button variant="ghost" :active="selected" class="whitespace-nowrap text-sm">
                  {{ I18n.t("editor.magic") }}
                </Button>
              </Tab>
              <Tab v-slot="{ selected }" as="template">
                <Button variant="ghost" :active="selected" class="text-sm">{{ I18n.t("editor.photo") }}</Button>
              </Tab>
              <Tab v-slot="{ selected }" as="template">
                <Button variant="ghost" :active="selected" class="text-sm">{{ I18n.t("editor.color") }}</Button>
              </Tab>
            </TabList>
          </div>

          <TabPanels key="panels">
            <!-- Start AI Generate -->
            <TransitionGroup name="slide" v-if="isMagicBackgroundsEnabled">
              <TabPanel>
                <InspirationGallery
                  :foreground-type="props.foregroundType"
                  :selected-image-state="props.selectedImageState"
                />
              </TabPanel>
            </TransitionGroup>

            <!-- Start Photo -->
            <TransitionGroup name="slide">
              <TabPanel>
                <BackgroundImageSearch
                  :foreground-type="props.foregroundType"
                  :selected-image-state="props.selectedImageState"
                />
              </TabPanel>
            </TransitionGroup>

            <!-- End Photo -->

            <!-- Start Color -->
            <TabPanel>
              <div class="divider"><hr /></div>
              <div class="scroll-container">
                <div class="sm:h-64 pb-4 gap-1 tile-grid-photos-new-layout">
                  <ColorTiles
                    :selected-image-state="props.selectedImageState"
                    :colors="availableColors"
                    image-button-size="flex"
                  />
                </div>
              </div>
              <div class="hidden md:block shadow-fold w-full h-2"></div>
            </TabPanel>
            <!-- End Color -->
          </TabPanels>
        </TabGroup>
      </div>
    </template>
  </Drawer>
</template>

<script setup lang="ts">
import "@/src/i18n";

import { TabGroup, TabList, Tab, TabPanels, TabPanel } from "@headlessui/vue";
import { Button, Drawer } from "prism";
import ColorTiles from "@/components/prism/color_tiles.vue";
import BackgroundImageSearch from "@/components/upload/add_background/background_image_search.vue";
import { ref, inject } from "vue";
import { PersistentStore } from "@/stores/persistent_store";
import { useEditorStore } from "@/stores/editor_store";
import InspirationGallery from "@/components/upload/ai_generate/inspiration_gallery.vue";
import { rbgEditorBackgroundPanelTabClickedV100 } from "kaleido-event-tracker";
import { usePhotoEditor } from "@/composables/photo_editor";
import { useMagicBackgroundFlag } from "@/composables/magic_background_flag";

const I18n = inject("I18n");

const store = useEditorStore();
const { isNewEditorEnabled: isNewPhotoEditor } = usePhotoEditor();

export type AddBackgroundTabs = "ai_scene" | "photo" | "color";

interface AddBackgroundProps {
  foregroundType: string;
  selectedImageState: PersistentStore;
}

const tabIndexToName: Record<number, AddBackgroundTabs> = {
  0: "ai_scene",
  1: "photo",
  2: "color",
};

const nameToTabIndex = (tabName: string) => {
  const tabIndex = Number(Object.entries(tabIndexToName).find(([_index, name]) => name === tabName)?.[0] ?? 0);
  return isNaN(tabIndex) ? 0 : tabIndex;
};

const selectedTab = ref(nameToTabIndex(store.openedBackgroundTab));

const { isMagicBackgroundsEnabled } = useMagicBackgroundFlag();

const props = defineProps<AddBackgroundProps>();
const availableColors = ref<string[]>([
  ...store.backgroundColorSamples,
  "#ffffff",
  "#f44336",
  "#e91e63",
  "#9c27b0",
  "#673ab7",
  "#3f51b5",
  "#2196f3",
  "#03a9f4",
  "#00bcd4",
  "#009688",
  "#4caf50",
  "#8bc34a",
  "#cddc39",
  "#ffeb3b",
  "#ffc107",
  "#ff9800",
  "#ff5722",
  "#795548",
  "#9e9e9e",
  "#607d8b",
  "#000000",
]);

const emit = defineEmits<{
  (e: "closePanel"): void;
}>();

const closePanel = () => {
  emit("closePanel");
};

const onTabChanged = (index: number) => {
  selectedTab.value = index;
  const tabName = tabIndexToName[index];
  store.openedBackgroundTab = tabName;
  if (isMagicBackgroundsEnabled.value) {
    rbgEditorBackgroundPanelTabClickedV100({ image_id: store.selectedImage.meta.id, panel_tab_name: tabName });
  }
};
</script>

<style lang="scss" scoped>
.tile-grid-photos-new-layout {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));

  @media (min-width: 368px) and (max-width: 1023px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

.content-wrapper-legacy-editor {
  @media (min-width: 1024px) {
    width: 16.5rem;
  }
}

.content-wrapper {
  max-height: 30rem;
  width: 100%;

  @media (max-width: 1024px) {
    height: 24rem;
  }
}

.tab-list-scroll-container {
  overflow-x: auto;
  min-height: fit-content;
}

.slide-move {
  transition: all 0.15s ease;
}
.slide-leave-active {
  position: absolute;
}

.divider {
  margin: 0 -16px !important;

  @media (min-width: 640px) {
    margin: 0 -24px !important;
  }
}

.scroll-container {
  overflow-x: hidden;
  overflow-y: auto;

  @media (max-width: 1024px) {
    height: 19.5rem;
  }
}

/* this feature is only supported on ios, it's used here because scroll behavior is different on ios and negative margin shifts the content */
/* @supports not (-webkit-touch-callout: none) {
  .scroll-container {
    margin-right: -1rem;
  }
} */

/* .scroll-container::-webkit-scrollbar {
  -webkit-appearance: none;
  width: 1rem;
} */

.tab-list-scroll-container::-webkit-scrollbar {
  -webkit-appearance: none;
  height: 0;
}

.scroll-container::-webkit-scrollbar-thumb {
  border: 4px solid rgba(0, 0, 0, 0);
  background-clip: padding-box;
  -webkit-border-radius: 7px;
  background-color: rgba(0, 0, 0, 0.15);
  -webkit-box-shadow: inset -1px -1px 0px rgba(0, 0, 0, 0.05), inset 1px 1px 0px rgba(0, 0, 0, 0.05);
}
</style>
